/**
 * RESPONSIBILITY: how high an event sits in the lists, for one stretch of time.
 *
 * A priority is not a number an event has; it is a number an event has RIGHT NOW. A
 * retreat is the most important thing on the site for the three weeks before it and
 * ordinary the day after, and the only way to say that with a single number is for
 * somebody to remember to come back and change it. Nobody remembers.
 *
 * An event holds a list of these. Which one applies is decided by the clock — see
 * priority-now.ts — and an event with no windows at all keeps the position it was given
 * by hand, so nothing that worked before this existed stopped working.
 *
 * The number means what `position` means, because it is the same number: 1 is the top,
 * and 0 is "sort me by date".
 *
 * BOTH DATES ARE OPTIONAL. Empty means unbounded on that side, so a row with neither is
 * just a priority that always applies. Demanding both made a half-filled row do nothing
 * whatsoever, and a setting that silently does nothing is worse than one that is wrong.
 */
import { Row, DateField, Num } from '@framework/core/decorators';

@Row()
export class PriorityRow {
  @DateField({
    label: 'From',
    description: 'The first day this applies. Empty means it has always applied.',
  })
  from!: string;

  @DateField({
    label: 'Until',
    description: 'The last day it applies. Empty means it never stops.',
  })
  until!: string;

  @Num({
    label: 'Priority', min: 0, max: 999, integer: true, default: 0,
    description: '1 is the top of the list, 2 below it. 0 sorts by date like the rest.',
  })
  priority!: number;
}
